import React, {useState} from 'react';
import {
    Create,
    Edit,
    SimpleForm,
    required,
    useRecordContext,
    usePermissions,
    useSaveContext,
    useTranslate,
} from 'react-admin';
import {useFormContext} from 'react-hook-form';
import {FormatButtons, RichTextInput, RichTextInputToolbar} from 'ra-input-rich-text';
import {Box, Typography} from '@mui/material';
import {DeleteOutline} from '@mui/icons-material';
import {t} from '../themes/statamicTokens';
import PageHeader from '../components/statamic/PageHeader';
import PrimaryButton from '../components/statamic/PrimaryButton';
import SectionCard from '../components/statamic/SectionCard';
import StatusDot from '../components/statamic/StatusDot';
import StatamicTextInput from '../components/statamic/StatamicTextInput';
import StatamicSelectInput from '../components/statamic/StatamicSelectInput';
import DeleteConfirmDialog from '../components/statamic/DeleteConfirmDialog';
import useDeleteConfirm from '../components/statamic/useDeleteConfirm';
import MailVariablesLegend from './MailVariablesLegend';
import MailTestPanel from './MailTestPanel';
import {useUmbrellaChoices} from '../lib/customHooks';
import hasPermission from '../auth/hasPermission';

const LANGUAGE_CHOICES = (translate) => [
    {id: 'hy', name: translate('hy')},
    {id: 'de', name: translate('de')},
    {id: 'en', name: translate('en')},
    {id: 'nl', name: translate('nl')},
    {id: 'ru', name: translate('ru')},
];

const LANGUAGE_LABEL = {
    nl: 'Nederlands',
    en: 'English',
    de: 'Deutsch',
    ru: 'Русский',
    hy: 'Հայերեն',
};

const SaveAction = ({label}) => {
    const {handleSubmit} = useFormContext();
    const {save, saving} = useSaveContext();
    return (
        <PrimaryButton variant="primary" disabled={saving} onClick={handleSubmit(save)}>
            {saving ? 'Saving…' : label}
        </PrimaryButton>
    );
};

const RTIToolbar = () => (
    <RichTextInputToolbar>
        <FormatButtons/>
    </RichTextInputToolbar>
);

const RichTextField = ({source, label, required: req}) => (
    <Box sx={{mb: 2}}>
        <Typography
            sx={{
                fontSize: `${t.type.caption.size}px`,
                fontWeight: 500,
                color: t.color.text.secondary,
                mb: 0.5,
            }}
        >
            {label}
            {req && (
                <Box component="span" sx={{color: t.color.status.dangerSolid, ml: 0.25}}>*</Box>
            )}
        </Typography>
        <Box
            sx={{
                borderRadius: `${t.radius.sm}px`,
                border: `1px solid ${t.color.border.default}`,
                backgroundColor: t.color.surface,
                overflow: 'hidden',
                '&:focus-within': {
                    borderColor: t.color.accent.primary,
                    boxShadow: `0 0 0 3px ${t.color.accent.primaryTint}`,
                },
                '& .RaRichTextInput-editorContent': {
                    padding: '8px 12px',
                    minHeight: 80,
                    fontSize: `${t.type.body.size}px`,
                    fontFamily: t.font.sans,
                    color: t.color.text.primary,
                },
                '& .RaRichTextInput-editorContent p': {margin: 0, marginBottom: '8px'},
                '& .MuiToolbar-root': {
                    borderBottom: `1px solid ${t.color.border.hairline}`,
                    minHeight: '36px !important',
                    px: 1,
                    backgroundColor: t.color.sidebarHover,
                },
                '& .MuiFormHelperText-root:not(.Mui-error)': {display: 'none'},
                '& .MuiFormHelperText-root.Mui-error': {
                    fontSize: 11,
                    color: t.color.status.dangerSolid,
                    mx: 0,
                    mt: 0.5,
                },
                '& .RaLabeled-label, & label': {display: 'none'},
            }}
        >
            <RichTextInput
                toolbar={<RTIToolbar/>}
                fullWidth
                label={false}
                source={source}
                validate={req ? required() : undefined}
            />
        </Box>
    </Box>
);

const TabSwitcher = ({value, onChange, tabs}) => (
    <Box
        sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            borderBottom: `1px solid ${t.color.border.default}`,
            px: 2,
        }}
    >
        {tabs.map(tab => {
            const active = value === tab.id;
            return (
                <Box
                    key={tab.id}
                    onClick={() => onChange(tab.id)}
                    sx={{
                        px: 2,
                        py: 1.25,
                        cursor: 'pointer',
                        fontSize: `${t.type.small.size}px`,
                        fontWeight: active ? 600 : 500,
                        color: active ? t.color.accent.primary : t.color.text.secondary,
                        borderBottom: active
                            ? `2px solid ${t.color.accent.primary}`
                            : '2px solid transparent',
                        marginBottom: '-1px',
                        '&:hover': {color: t.color.text.primary},
                    }}
                >
                    {tab.label}
                </Box>
            );
        })}
    </Box>
);

const FormBody = ({mode}) => {
    const record = useRecordContext();
    const translate = useTranslate();
    const {permissions} = usePermissions();
    const umbrellaChoices = useUmbrellaChoices();
    const deleteConfirm = useDeleteConfirm();
    const [activeTab, setActiveTab] = useState('registration');

    const canPickUmbrella = hasPermission(permissions, ['view'], 'umbrella');

    if (!umbrellaChoices) return null;
    if (mode === 'edit' && !record) return null;

    const langLabel = LANGUAGE_LABEL[record?.language_code] || String(record?.language_code || '').toUpperCase();
    const headerTitle = mode === 'create'
        ? translate('Create mail settings')
        : `${record?.charity_name ?? `Umbrella #${record?.charity_id}`} · ${langLabel}`;

    const tabs = [
        {id: 'registration', label: translate('Registration mail')},
        {id: 'reminder',     label: translate('Reminder for new posts')},
        {id: 'daily',        label: translate('Daily new post mail')},
    ];

    return (
        <Box sx={{backgroundColor: t.color.page, minHeight: '100%'}}>
            <PageHeader
                breadcrumb={{label: translate('Mail settings'), to: '/mailSettings'}}
                title={
                    mode === 'create' ? (
                        <Box component="span">{headerTitle}</Box>
                    ) : (
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                            <StatusDot variant="success"/>
                            <Box component="span">{headerTitle}</Box>
                        </Box>
                    )
                }
                actions={
                    <>
                        <SaveAction label={mode === 'create' ? translate('Create') : translate('Save changes')}/>
                        {mode === 'edit' && permissions === 'admin' && (
                            <PrimaryButton
                                variant="danger"
                                startIcon={<DeleteOutline sx={{fontSize: 16}}/>}
                                onClick={deleteConfirm.openDialog}
                            >
                                {translate('Delete')}
                            </PrimaryButton>
                        )}
                    </>
                }
            />

            {mode === 'edit' && record && (
                <DeleteConfirmDialog
                    open={deleteConfirm.open}
                    onClose={deleteConfirm.closeDialog}
                    resource="mailSettings"
                    record={record}
                    recordLabel={`${record.charity_name ?? 'Mail settings'} · ${langLabel}`}
                />
            )}

            <Box sx={{p: 3, display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 360px', gap: 2, alignItems: 'start'}}>
                <SectionCard>
                    <Box sx={{px: 2, pt: 2}}>
                        <Box sx={{display: 'grid', gridTemplateColumns: canPickUmbrella ? '1fr 1fr' : '1fr', gap: 2}}>
                            {canPickUmbrella && (
                                <StatamicSelectInput
                                    source="charity_id"
                                    label={translate('Umbrella')}
                                    required
                                    choices={umbrellaChoices}
                                />
                            )}
                            <StatamicSelectInput
                                source="language_code"
                                label={translate('Language')}
                                required
                                choices={LANGUAGE_CHOICES(translate)}
                                defaultValue="nl"
                            />
                        </Box>
                    </Box>

                    <Box sx={{mt: 2}}>
                        <TabSwitcher value={activeTab} onChange={setActiveTab} tabs={tabs}/>
                    </Box>

                    <Box sx={{p: 2}}>
                        {activeTab === 'registration' && (
                            <>
                                <RichTextField source="welcome_text"     label={translate('Welcome text')} required/>
                                <RichTextField source="app_text_before"  label={translate('App text before')} required/>
                                <RichTextField source="payment_iban"     label={translate('Payment IBAN text')}/>
                                <RichTextField source="payment_other"    label={translate('Payment other text')}/>
                                <RichTextField source="regards_text"     label={translate('Regards text')} required/>
                                <RichTextField source="address_text"     label={translate('Address text')}/>
                                <StatamicTextInput
                                    source="logo_url"
                                    label={translate('Logo URL')}
                                />
                            </>
                        )}
                        {activeTab === 'reminder' && (
                            <>
                                <StatamicTextInput
                                    source="update_subject"
                                    label={translate('Subject')}
                                />
                                <Box sx={{mt: 2}}>
                                    <RichTextField source="update_pre_text"  label={translate('Update pre text')}/>
                                    <RichTextField source="update_post_text" label={translate('Update post text')}/>
                                </Box>
                            </>
                        )}
                        {activeTab === 'daily' && (
                            <>
                                <StatamicTextInput
                                    source="new_post_subject"
                                    label={translate('Subject')}
                                />
                                <Box sx={{mt: 2}}>
                                    <RichTextField source="pre_text"  label={translate('New post pre text')}/>
                                    <RichTextField source="post_text" label={translate('New post post text')}/>
                                </Box>
                                <StatamicTextInput
                                    source="test_update_post_id"
                                    label="Update post test ID"
                                />
                            </>
                        )}
                    </Box>
                </SectionCard>

                <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, position: 'sticky', top: 16}}>
                    {mode === 'edit' && (
                        <SectionCard title={translate('Send test mail')}>
                            <MailTestPanel/>
                        </SectionCard>
                    )}
                    <SectionCard title={translate('Template variables')}>
                        <MailVariablesLegend/>
                    </SectionCard>
                </Box>
            </Box>
        </Box>
    );
};

export const MailSettingsEditStatamic = (props) => (
    <Edit {...props} actions={false} mutationMode="pessimistic" component={Box} redirect="edit">
        <SimpleForm toolbar={false}>
            <FormBody mode="edit"/>
        </SimpleForm>
    </Edit>
);

export const MailSettingsCreateStatamic = (props) => (
    <Create {...props} actions={false} mutationMode="pessimistic" component={Box} redirect="edit">
        <SimpleForm toolbar={false}>
            <FormBody mode="create"/>
        </SimpleForm>
    </Create>
);

export default MailSettingsEditStatamic;
